import { Attr, BelongsTo, Model } from 'spraypaint';
import ApplicationRecord from '../ApplicationRecord';
import User from './User';

/**
 * A staff member marking an account verified by hand (P5-03) - the phone-in
 * override, for the bidder who cannot work the emailed link.
 *
 * A create-only action resource rather than a writable boolean on the user, and
 * that is the whole design: it is a privileged override of an identity check, so
 * it is as visible in the API as it is in consequence. Server-side,
 * `mark_verified(by=...)` writes this append-only audit row and the summary
 * columns on the user in one transaction, and it is the only thing that writes
 * either.
 *
 * Reading the rows back over the API is a `405`. The user's `verifiedManually`
 * and `verifiedByName` are the summary to display; the full history is a
 * django-admin question.
 *
 * `400` with `code: "already_verified"` if the account is already confirmed.
 * That is not merely redundant - a second override would move `verifiedAt` and
 * record a staff member as having vouched for someone who proved the address
 * themselves.
 */
@Model()
export default class VerificationOverride extends ApplicationRecord {
  static jsonapiType = 'verification-overrides';

  /**
   * Verify the given user by hand, and read the refreshed user back.
   *
   * `returnScope` puts `?include=user` on the POST - the only way to get an
   * include onto a write, and the same trick `TermsAcceptance` uses - so the
   * `201` sideloads the updated user and the page can re-render its status
   * without a second request.
   */
  static async mark(
    user: User,
  ): Promise<{ ok: boolean; user: User | null; overriddenAt: string }> {
    const override = new VerificationOverride();
    override.user = user;
    const ok = await override.save({
      with: 'user.id',
      returnScope: VerificationOverride.includes('user'),
    });
    return {
      ok,
      user: override.user || null,
      overriddenAt: override.overriddenAt,
    };
  }

  // Who was verified. Sent on create, and returned on the audit row.
  @BelongsTo() user: User;

  // Read-only, server-set: who performed the override, and when. `staff` is a
  // relationship rather than the plain name string the user resource carries,
  // so it is only populated when explicitly included.
  @BelongsTo() staff: User;

  @Attr() overriddenAt: string;
}
